import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  validateShipmentDocuments,
  crossValidateDocuments,
  mergeDocuments,
  uploadShipmentDocuments,
  uploadShipmentDocument,
  deleteShipmentDocument,
  guessDocumentType,
  getDocumentDownloadUrl,
  ShipmentDocument,
  ExtractionResult,
  useDocumentStatusSubscription,
} from '@wove/api'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Checkbox,
  Spinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  cn,
} from '@wove/web-ui'
import { SplitScreenDocumentDialog } from '@wove/document-extraction'
import {
  FileText,
  Upload,
  Download,
  Eye,
  Trash2,
  CheckCircle,
  AlertCircle,
  GitMerge,
  Shield,
  RefreshCw,
  Wifi,
  WifiOff,
  Clock,
} from 'lucide-react'
import { format } from 'date-fns'
import { DocumentExtractionStatus } from '@wove/common'
import { CrossValidationDialog } from './CrossValidationDialog'
import { MergeDocumentsDialog } from './MergeDocumentsDialog'
import { JobHistoryDialog } from './JobHistoryDialog'
import { ValidationItemsPanel } from './ValidationItemsPanel'

interface DocumentsTabProps {
  shipmentId: string
  documents: ShipmentDocument[]
  isLoading: boolean
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ shipmentId, documents, isLoading }) => {
  const queryClient = useQueryClient()
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<ShipmentDocument | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadDocumentType, setUploadDocumentType] = useState<string>('commercialInvoice')
  const [isDragging, setIsDragging] = useState(false)
  const [isMainDragging, setIsMainDragging] = useState(false)
  const [isGuessingType, setIsGuessingType] = useState(false)
  const [crossValidationDialogOpen, setCrossValidationDialogOpen] = useState(false)
  const [mergeDocumentsDialogOpen, setMergeDocumentsDialogOpen] = useState(false)
  const [jobHistoryDialogOpen, setJobHistoryDialogOpen] = useState(false)

  // Subscribe to document status updates
  const {
    documentStatuses,
    loading: subscriptionLoading,
    error: subscriptionError,
    connectionStatus,
    isReconnecting,
    reconnectCount,
  } = useDocumentStatusSubscription(shipmentId, documents)

  const uploadMutation = useMutation({
    mutationFn: (data: { file: File; documentType: string }) =>
      uploadShipmentDocument(shipmentId, data.file, data.documentType),
    onSuccess: (result) => {
      console.log('Upload successful:', result)
      // Force refetch of documents to get the latest data
      queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId, 'documents'] })
      queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId] })
      setUploadModalOpen(false)
      setUploadFile(null)
      setUploadDocumentType('commercialInvoice')
    },
    onError: (error) => {
      console.error('Upload error:', error)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => deleteShipmentDocument(shipmentId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId, 'documents'] })
      setDeleteModalOpen(false)
    },
  })

  const validateMutation = useMutation({
    mutationFn: () => validateShipmentDocuments(shipmentId, selectedDocIds),
  })


  const handleSelectAll = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([])
    } else {
      setSelectedDocIds(documents.map((doc) => doc.id))
    }
  }

  const handleSelectDocument = (docId: string) => {
    if (selectedDocIds.includes(docId)) {
      setSelectedDocIds(selectedDocIds.filter((id) => id !== docId))
    } else {
      setSelectedDocIds([...selectedDocIds, docId])
    }
  }

  const handleViewDocument = (doc: ShipmentDocument) => {
    setSelectedDocument(doc)
    setDocumentDialogOpen(true)
  }

  const handleDeleteDocument = (docId: string) => {
    setDocumentToDelete(docId)
    setDeleteModalOpen(true)
  }

  const handleUpload = () => {
    if (uploadFile) {
      uploadMutation.mutate({ file: uploadFile, documentType: uploadDocumentType })
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadFile(file)
      await guessTypeFromFileName(file.name)
    }
  }

  const guessTypeFromFileName = async (fileName: string) => {
    setIsGuessingType(true)
    try {
      const result = await guessDocumentType(shipmentId, fileName)
      if (result.success && result.documentType !== 'other') {
        setUploadDocumentType(result.documentType)
      }
    } catch (error) {
      console.error('Error guessing document type:', error)
      // Silently fail - keep the current selection
    } finally {
      setIsGuessingType(false)
    }
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      setUploadFile(file)
      await guessTypeFromFileName(file.name)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  // Main drag and drop handlers for the entire documents area
  const handleMainDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsMainDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      setUploadFile(file)
      await guessTypeFromFileName(file.name)
      setUploadModalOpen(true)
    }
  }

  const handleMainDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsMainDragging(true)
  }

  const handleMainDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // Only set dragging to false if we're leaving the main container
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY

    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsMainDragging(false)
    }
  }

  const getDocumentTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      commercialInvoice: '📄',
      packingList: '📋',
      billOfLading: '🚢',
      airWaybill: '✈️',
      certificateOfOrigin: '🏛️',
      dangerousGoodsDeclaration: '⚠️',
      other: '📎',
    }
    return icons[type] || '📎'
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { variant: 'success' | 'info' | 'error' | 'secondary'; icon: React.ReactNode }
    > = {
      [DocumentExtractionStatus.COMPLETED]: { variant: 'success', icon: <CheckCircle size={14} /> },
      [DocumentExtractionStatus.PROCESSING]: {
        variant: 'info',
        icon: <RefreshCw size={14} className="animate-spin" />,
      },
      [DocumentExtractionStatus.FAILED]: { variant: 'error', icon: <AlertCircle size={14} /> },
      [DocumentExtractionStatus.PENDING]: { variant: 'secondary', icon: null },
    }

    const config = statusConfig[status] || statusConfig[DocumentExtractionStatus.PENDING]

    // Debug: log unrecognized status
    if (!statusConfig[status]) {
      console.warn(`Unknown document status: "${status}"`)
    }

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        {config.icon}
        {status}
      </Badge>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <style>
        {`
          @keyframes slow-pulse {
            0%, 100% {
              opacity: 1;
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
            }
            50% {
              opacity: 0.85;
              box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
            }
          }
        `}
      </style>
      <div
        className={cn(
          'space-y-4 min-h-[400px] relative',
          isMainDragging &&
            'bg-blue-50 border-2 border-dashed border-blue-400 rounded-lg transition-colors',
        )}
        onDrop={handleMainDrop}
        onDragOver={handleMainDragOver}
        onDragLeave={handleMainDragLeave}
      >
        {/* Drag overlay */}
        {isMainDragging && (
          <div className="absolute inset-0 bg-blue-50 bg-opacity-90 z-10 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <Upload className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <p className="text-lg font-medium text-blue-700">Drop file here to upload</p>
              <p className="text-sm text-blue-600 mt-2">Release to upload your document</p>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Checkbox
              checked={selectedDocIds.length === documents.length && documents.length > 0}
              // indeterminate={selectedDocIds.length > 0 && selectedDocIds.length < documents.length}
              onChange={handleSelectAll}
            />
            <span className="text-sm text-gray-600">
              {selectedDocIds.length} of {documents.length} selected
            </span>

            {/* Real-time connection status */}
            {documents.length > 0 && (
              <div className="flex items-center gap-1 ml-4">
                {connectionStatus === 'connected' && (
                  <>
                    <Wifi className="h-4 w-4 text-green-500" />
                    <span className="text-xs text-green-500">Live updates</span>
                  </>
                )}
                {connectionStatus === 'connecting' && (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin text-amber-500" />
                    <span className="text-xs text-amber-500">
                      {isReconnecting ? `Reconnecting (${reconnectCount})` : 'Connecting...'}
                    </span>
                  </>
                )}
                {connectionStatus === 'disconnected' && (
                  <>
                    <WifiOff className="h-4 w-4 text-red-500" />
                    <span className="text-xs text-red-500">Disconnected</span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={16} />}
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId, 'documents'] })
                queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId] })
                console.log('Manually refreshing documents...')
              }}
              title="Refresh documents"
            >
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Clock size={16} />}
              onClick={() => setJobHistoryDialogOpen(true)}
              title="View job history"
            >
              History
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Upload size={16} />}
              onClick={() => setUploadModalOpen(true)}
            >
              Upload
            </Button>

            {selectedDocIds.length > 0 && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Shield size={16} />}
                  onClick={() => validateMutation.mutate()}
                  loading={validateMutation.isPending}
                >
                  Validate
                </Button>

                {selectedDocIds.length > 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Shield size={16} />}
                      onClick={() => setCrossValidationDialogOpen(true)}
                    >
                      Cross-Validate
                    </Button>

                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<GitMerge size={16} />}
                      onClick={() => setMergeDocumentsDialogOpen(true)}
                    >
                      Merge
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Documents List */}
        {documents.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-500 mb-4">No documents uploaded yet</p>
              <Button leftIcon={<Upload size={16} />} onClick={() => setUploadModalOpen(true)}>
                Upload Document
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => {
              // Use subscription status if available, otherwise use document's status
              const currentStatus =
                documentStatuses[doc.id]?.extractionStatus ||
                doc.extractionStatus ||
                DocumentExtractionStatus.PENDING
              const isProcessing = currentStatus === DocumentExtractionStatus.PROCESSING
              const statusChanged =
                documentStatuses[doc.id]?.extractionStatus &&
                documentStatuses[doc.id]?.extractionStatus !== doc.extractionStatus

              return (
                <Card
                  key={doc.id}
                  className={cn(
                    'hover:shadow-md transition-all duration-300',
                    isProcessing && 'border-blue-400',
                    statusChanged && 'border-green-400 bg-green-50',
                  )}
                  style={
                    isProcessing
                      ? {
                          animation: 'slow-pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                        }
                      : undefined
                  }
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={selectedDocIds.includes(doc.id)}
                        onChange={() => handleSelectDocument(doc.id)}
                      />

                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getDocumentTypeIcon(doc.type)}</span>
                            <div>
                              <h4 className="font-medium">{doc.name}</h4>
                              <p className="text-sm text-gray-500">{doc.type}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {getStatusBadge(currentStatus)}
                            <span className="text-sm text-gray-500">
                              {format(new Date(doc.createdAt), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>

                        {(doc.extractedData ||
                          documentStatuses[doc.id]?.extractionStatus ===
                            DocumentExtractionStatus.COMPLETED) && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Extracted:</span>{' '}
                            {doc.extractedData ? Object.keys(doc.extractedData).length : '...'}{' '}
                            fields
                            {documentStatuses[doc.id]?.extractionStatus ===
                              DocumentExtractionStatus.COMPLETED &&
                              !doc.extractedData && (
                                <span className="text-green-600 ml-2">(Just completed!)</span>
                              )}
                          </div>
                        )}

                        {documentStatuses[doc.id]?.extractionError && (
                          <div className="mt-2 text-sm text-red-600">
                            <span className="font-medium">Error:</span>{' '}
                            {documentStatuses[doc.id].extractionError}
                          </div>
                        )}

                        {/* Show validation status */}
                        {(doc.validationStatus || doc.crossValidationResult) && (
                          <div className="mt-2 flex items-center gap-2">
                            {doc.validationStatus && (
                              <Badge
                                variant={doc.validationStatus === 'valid' ? 'success' : 'error'}
                              >
                                {doc.validationStatus === 'valid' ? 'Valid' : 'Has Issues'}
                              </Badge>
                            )}
                            {doc.crossValidationResult && (
                              <div className="flex items-center gap-1">
                                <Shield size={14} className="text-blue-500" />
                                <span className="text-xs text-gray-600">
                                  Cross-validated:{' '}
                                  {doc.crossValidationResult.validatedFields?.length || 0} fields,{' '}
                                  {doc.crossValidationResult.issues?.length || 0} issues
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Eye size={16} />}
                          onClick={() => handleViewDocument(doc)}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Download size={16} />}
                          onClick={async () => {
                            const downloadUrl = getDocumentDownloadUrl(shipmentId, doc.id)
                            const token = localStorage.getItem('token')
                            if (token) {
                              try {
                                const response = await fetch(downloadUrl, {
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                  },
                                })
                                if (response.ok) {
                                  const blob = await response.blob()
                                  const url = window.URL.createObjectURL(blob)
                                  const a = document.createElement('a')
                                  a.href = url
                                  a.download = doc.name
                                  document.body.appendChild(a)
                                  a.click()
                                  window.URL.revokeObjectURL(url)
                                  document.body.removeChild(a)
                                } else {
                                  console.error('Failed to download document')
                                }
                              } catch (error) {
                                console.error('Error downloading document:', error)
                              }
                            }
                          }}
                        >
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 size={16} />}
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        {/* Validation Items Panel */}
        {documents.length > 0 && (
          <div className="mt-8">
            <ValidationItemsPanel shipmentId={shipmentId} />
          </div>
        )}

        {/* Document Dialog */}
        {selectedDocument && (
          <SplitScreenDocumentDialog
            isOpen={documentDialogOpen}
            onClose={() => {
              setDocumentDialogOpen(false)
              setSelectedDocument(null)
            }}
            shipmentId={shipmentId}
            document={selectedDocument}
            onUpdate={() => {
              queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId, 'documents'] })
            }}
          />
        )}

        {/* Upload Modal */}
        <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="document-type">
                  Document Type
                  {isGuessingType && (
                    <span className="ml-2 text-sm font-normal text-blue-600">
                      (AI is analyzing filename...)
                    </span>
                  )}
                </Label>
                <Select
                  value={uploadDocumentType}
                  onValueChange={setUploadDocumentType}
                  disabled={isGuessingType}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* TODO: make this dynamic from the backend */}
                    <SelectItem value="CommercialInvoice">Commercial Invoice</SelectItem>
                    <SelectItem value="PackingList">Packing List</SelectItem>
                    <SelectItem value="BillOfLading">Bill of Lading</SelectItem>
                    <SelectItem value="AirWaybill">Air Waybill</SelectItem>
                    <SelectItem value="CertificateOfOrigin">Certificate of Origin</SelectItem>
                    <SelectItem value="DangerousGoodsDeclaration">
                      Dangerous Goods Declaration
                    </SelectItem>
                    <SelectItem value="InsuranceCertificate">Insurance Certificate</SelectItem>
                    <SelectItem value="SafetyDataSheet">Safety Data Sheet</SelectItem>
                    <SelectItem value="BookingConfirmation">Booking Confirmation</SelectItem>
                    <SelectItem value="APInvoice">AP Invoice</SelectItem>
                    <SelectItem value="ARInvoice">AR Invoice</SelectItem>
                    <SelectItem value="PaymentConfirmation">Payment Confirmation</SelectItem>
                    <SelectItem value="ISFFiling">ISF Filing</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="file-upload">Select or Drop File</Label>
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={cn(
                    'mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors',
                    isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300',
                    uploadFile && 'bg-gray-50',
                  )}
                >
                  <div className="space-y-1 text-center">
                    {uploadFile ? (
                      <>
                        <FileText className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="text-sm text-gray-900">{uploadFile.name}</p>
                        <p className="text-xs text-gray-500">
                          {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </>
                    ) : (
                      <>
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                          >
                            <span>Upload a file</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              className="sr-only"
                              onChange={handleFileChange}
                              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PDF, PNG, JPG, XLSX, DOC up to 10MB</p>
                      </>
                    )}
                  </div>
                </div>
                {uploadFile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => setUploadFile(null)}
                  >
                    Remove file
                  </Button>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => {
                  setUploadModalOpen(false)
                  setUploadFile(null)
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!uploadFile}
                loading={uploadMutation.isPending}
              >
                Upload
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Document</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p>Are you sure you want to delete this document? This action cannot be undone.</p>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => documentToDelete && deleteMutation.mutate(documentToDelete)}
                loading={deleteMutation.isPending}
              >
                Delete Document
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cross-Validation Dialog */}
        <CrossValidationDialog
          open={crossValidationDialogOpen}
          onOpenChange={setCrossValidationDialogOpen}
          shipmentId={shipmentId}
          documentIds={selectedDocIds}
          documents={documents}
        />

        {/* Merge Documents Dialog */}
        <MergeDocumentsDialog
          open={mergeDocumentsDialogOpen}
          onOpenChange={setMergeDocumentsDialogOpen}
          shipmentId={shipmentId}
          documentIds={selectedDocIds}
          documents={documents}
        />

        {/* Job History Dialog */}
        <JobHistoryDialog
          open={jobHistoryDialogOpen}
          onOpenChange={setJobHistoryDialogOpen}
          shipmentId={shipmentId}
        />
      </div>
    </>
  )
}

export default DocumentsTab
